'use client';

import React, { useState } from 'react';
import { stopImpersonating } from '@/app/lib/auth-client';
import { logger } from '@/app/lib/logger';
import { AlertTriangle, LogOut } from 'lucide-react';

interface ImpersonationBannerProps {
  impersonatorId: string; // ID of the admin doing the impersonating
  impersonatedUserId: string; // ID of the user being impersonated (optional, for display)
  impersonatedUserName?: string; // Optional name/email for display
}

export default function ImpersonationBanner({ 
  impersonatorId, 
  impersonatedUserId, 
  impersonatedUserName 
}: ImpersonationBannerProps) {
  const [isStopping, setIsStopping] = useState(false);

  const handleStopImpersonating = async () => {
    setIsStopping(true);
    try {
      await stopImpersonating();
      // Reload the page to end the impersonation session fully
      window.location.reload();
    } catch (error) {
      logger.error("Failed to stop impersonating", { error });
      alert("Failed to stop impersonating. Please try again."); // Simple feedback
      setIsStopping(false);
    }
  };

  const displayName = impersonatedUserName || `User ID: ${impersonatedUserId}`;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-yellow-500 text-yellow-900 p-3 shadow-md flex items-center justify-between text-sm">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" />
        <span>
          You are currently impersonating <strong>{displayName}</strong>. 
        </span>
      </div>
      <button
        onClick={handleStopImpersonating}
        disabled={isStopping}
        className="flex items-center gap-1 px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded-sm text-xs font-medium disabled:opacity-70 disabled:cursor-not-allowed"
      >
        <LogOut className="w-3.5 h-3.5" />
        {isStopping ? 'Stopping...' : 'Stop Impersonating'}
      </button>
    </div>
  );
} 